import { Typography } from '@navi/web-ui/lib/primitives';
import styles from '../../AgentForm.module.scss';
import AgentFormTextbox from './AgentFormTextbox';
import { ENTITY_TYPE, VALIDATION_MESSAGE, VERIFICATION_MESSAGE } from '../../constants/constants';
import AgentFormDropdown from './AgentFormDropdown';
import AgentFormAutocompleteDropdown from './AgentFormAutocompleteDropdown';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { convertStringToDropdownOption, convertToDropdownOptions } from '../../utils/utils';
import AgentFormDownloadDraCertificate from './AgentFormDownloadDraCertificate';
import AgentFormUploadControl from './AgentFormUploadControl';
import AgentBaseLocation from './AgentBaseLocation';
import AgentFormRadioChip from './AgentFormRadioChip';
import VerificationComponent from './VerificationComponent';
import { useState } from 'react';
import WarningSection from './WarningSection';
import {
  handleLocationCoordinatesChange,
  handleVerification
} from '../../actions/agentFormActions';
import { IAgentFormProps } from '../../interfaces/types';
import AgentFormDownloadPan from './AgentFormDownloadPan';
import AgentFormDateInput from './AgentFormDateInput';
import ActiveNextMonthWrapper from './ActiveNextMonthWrapper';
import CorporateNumbersDrawer from './CorporateNumbersDrawer';

const ExternalFieldAgentForm = (props: IAgentFormProps) => {
  const {
    control,
    errors,
    isEditDisabled,
    watch,
    isEmailRequired,
    validateEmail,
    isNaviEmailRequired,
    isEmailChangeDisabled,
    agentData,
    getValues,
    getAgencyCode,
    managerList,
    setValue,
    isDraUploadAccessFeatureFlagEnabled,
    setIsSubmitButtonDisabled,
    reverseGeocodedAgentCity,
    reverseGeocodedAgentPincode,
    locationVerificationStatus,
    setLocationVerificationStatus,
    showLocationVerificationToast,
    setShowLocationVerificationToast,
    setReverseGeocodedAgentCity,
    setReverseGeocodedAgentPincode,
    isEditPanDetailsDisabled = false,
    showActiveNextMonthToggle = false,
    isAgentActiveNextMonth,
    setIsAgentActiveNextMonth,
    isAgentActiveNextMonthToggleDirty,
    setIsAgentActiveNextMonthToggleDirty
  } = props;

  const bucketGroup = useSelector((state: RootState) => state?.teamLeadDashboard?.bucketGroup);

  const BUCKET_GROUPS = bucketGroup || [];

  const [isPincodeMismatch, setIsPincodeMismatch] = useState(false);
  const [showVerifyButton, setShowVerifyButton] = useState(false);
  const [showCityPincode, setShowCityPincode] = useState(
    !!reverseGeocodedAgentCity && !!reverseGeocodedAgentPincode
  );
  const [isMisMatchedPincode, setMisMatchedPincode] = useState(false);
  const panVerificationStatus = agentData?.panCardView?.userDocument?.status || '';
  const handlePincodeVerification = async () => {
    handleVerification({
      getValues,
      setLocationVerificationStatus,
      setShowLocationVerificationToast,
      setShowCityPincode,
      setReverseGeocodedAgentCity,
      setReverseGeocodedAgentPincode,
      setMisMatchedEntity: setMisMatchedPincode,
      entity: ENTITY_TYPE.PINCODE
    });
  };

  const handleAgentPincodeChange = (value: string) => {
    setMisMatchedPincode(false);
    if (
      reverseGeocodedAgentPincode &&
      value &&
      value !== reverseGeocodedAgentPincode &&
      value.length === 6
    ) {
      setIsPincodeMismatch(true);
    } else {
      setIsPincodeMismatch(false);
    }
  };

  const handleCoordinatesChange = (value: string) => {
    handleLocationCoordinatesChange({
      setIsEntityMismatch: setIsPincodeMismatch,
      setLocationVerificationStatus,
      setShowVerifyButton,
      setShowCityPincode,
      setShowLocationVerificationToast
    });
  };

  return (
    <>
      <div className={'pt-4 pl-5 mt-4'}>
        <Typography variant={'h4'} color="var(--navi-color-gray-c2)">
          Agent Details
        </Typography>
      </div>
      <ActiveNextMonthWrapper
        showActiveNextMonthToggle={showActiveNextMonthToggle}
        isEditDisabled={isEditDisabled}
        isAgentActiveNextMonth={isAgentActiveNextMonth}
        setIsAgentActiveNextMonth={setIsAgentActiveNextMonth}
        isAgentActiveNextMonthToggleDirty={isAgentActiveNextMonthToggleDirty}
        setIsAgentActiveNextMonthToggleDirty={setIsAgentActiveNextMonthToggleDirty}
      />
      <div className={styles.inputContainer}>
        <AgentFormTextbox
          control={control}
          name="agentName"
          rules={{ required: true }}
          errors={errors}
          isEditDisabled={isEditDisabled || isEditPanDetailsDisabled}
          styles={styles}
          inputLabel={'Agent Name'}
          placeholder={'Enter here'}
          validationMessage={VALIDATION_MESSAGE.AGENT_NAME_MESSAGE}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormDateInput
          control={control}
          name={'dateOfBirth'}
          rules={{ required: false }}
          errors={errors}
          placeholder={'Select date'}
          inputLabel={'Date of birth (as per PAN)'}
          isEditDisabled={isEditDisabled || isEditPanDetailsDisabled}
          styles={styles}
          validationMessage={''}
          showRequiredIcon={false}
          defaultSelected={null}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormTextbox
          control={control}
          name="employeeId"
          rules={{ required: getAgencyCode() === '1000' }}
          errors={errors}
          isEditDisabled={isEditDisabled}
          styles={styles}
          inputLabel={'Employee ID'}
          placeholder={'Enter here'}
          validationMessage={VALIDATION_MESSAGE.AGENT_EMPLOYEE_ID_MESSAGE}
          isRequired={getAgencyCode() === '1000'}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormTextbox
          control={control}
          name="emailAddress"
          rules={{
            required: isEmailRequired(),
            validate: (value: string) => {
              if (watch('emailAddress')) {
                return validateEmail(value, isNaviEmailRequired);
              }
            }
          }}
          isRequired={isEmailRequired()}
          inputLabel={'Email ID'}
          placeholder={isEditDisabled && agentData.emailAddress === null ? 'NA' : 'Enter here'}
          isEditDisabled={isEditDisabled || isEmailChangeDisabled}
          errors={errors}
          styles={styles}
          validationMessage={
            isNaviEmailRequired
              ? VALIDATION_MESSAGE.NAVI_EMAIL_MESSAGE
              : VALIDATION_MESSAGE.EMAIL_MESSAGE
          }
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormTextbox
          control={control}
          name="phoneNumber"
          rules={{
            required: true,
            minLength: 10,
            maxLength: 10,
            pattern: /^[0-9]{10}$/i
          }}
          inputLabel={'Phone Number'}
          placeholder={'Enter here'}
          isEditDisabled={isEditDisabled}
          errors={errors}
          styles={styles}
          validationMessage={VALIDATION_MESSAGE.PHONE_NUMBER_MESSAGE}
        />
      </div>
      <CorporateNumbersDrawer isEditDisabled={isEditDisabled} agentData={agentData} />

      <div className={styles.inputContainer}>
        <AgentFormTextbox
          control={control}
          name="panNumber"
          rules={{
            required: true,
            pattern: /^[a-zA-Z0-9]{10}$/
          }}
          errors={errors}
          isEditDisabled={isEditDisabled || isEditPanDetailsDisabled}
          styles={styles}
          inputLabel={'PAN'}
          placeholder={'Enter PAN'}
          validationMessage={VALIDATION_MESSAGE.PAN_CARD_MESSAGE}
          isRequired={true}
          isUpperCaseRequired={true}
        />
      </div>

      {watch('panCardView') ? (
        <div className="w-[364px] ml-2 mt-4">
          <AgentFormDownloadPan
            control={control}
            name={'panCardView'}
            rules={{ required: true }}
            isEditDisable={isEditDisabled || isEditPanDetailsDisabled}
            inputLabel={'PAN card'}
            panVerificationStatus={panVerificationStatus}
            isRequired={true}
          />
        </div>
      ) : (
        <AgentFormUploadControl
          control={control}
          name={'panCard'}
          rules={{ required: true }}
          errors={errors}
          isEditDisabled={isEditDisabled || isEditPanDetailsDisabled}
          styles={{}}
          setSubmitButtonDisabled={setIsSubmitButtonDisabled}
          inputLabel={'PAN card'}
          documentType={'PAN_CARD'}
          documentContentType={'JPG'}
          supportedFile={'.jpeg, .jpg, .png'}
          isRequired={true}
        />
      )}

      <div className={styles.inputContainer}>
        <AgentFormDropdown
          control={control}
          name={'bucketGroup'}
          rules={{ required: true }}
          errors={errors}
          placeholder={'Select bucket group'}
          inputLabel={'Bucket Group'}
          isEditDisabled={isEditDisabled}
          styles={styles}
          validationMessage={VALIDATION_MESSAGE.BUCKET_GROUP_MESSAGE}
          options={convertToDropdownOptions(BUCKET_GROUPS)}
          defaultSelected={convertStringToDropdownOption(getValues('bucketGroup'))}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormAutocompleteDropdown
          control={control}
          name={'reportingManagerEmailAddress'}
          inputLabel={'Reporting Manager'}
          rules={{ required: true }}
          errors={errors}
          placeholder={'Type or Select reporting manager'}
          isEditDisabled={isEditDisabled}
          styles={styles}
          options={managerList}
          validationMessage={VALIDATION_MESSAGE.REPORTING_MANAGER_MESSAGE}
          updateClearAllCallback={() => {
            setValue('reportingManagerEmailAddress', '');
          }}
        />
      </div>

      <AgentBaseLocation
        latitude={'currentMonthLatitude'}
        longitude={'currentMonthLongitude'}
        control={control}
        errors={errors}
        isEditDisabled={true}
        callbackFn={handleCoordinatesChange}
      />

      <div className={styles.locationContainer}>
        <div className="flex">
          <Typography variant="p4" color="var(--navi-color-gray-c2)" className="pr-1">
            {'Agent Pincode '}
          </Typography>
          {
            <Typography variant="p4" color="var(--navi-color-gray-c3)">
              {'(for current month)'}
            </Typography>
          }
        </div>
        <div>
          <AgentFormTextbox
            control={control}
            name="currentMonthAgentPincode"
            rules={{
              required: false
            }}
            inputLabel={''}
            placeholder={'Enter 6-digit pincode'}
            isEditDisabled={true}
            errors={errors}
            styles={styles}
            validationMessage={VALIDATION_MESSAGE.PHONE_NUMBER_MESSAGE}
          />
        </div>
      </div>

      {watch('draCertificateView') ? (
        <div className="w-[364px] ml-2 mt-4">
          <AgentFormDownloadDraCertificate
            control={control}
            name={'draCertificateView'}
            rules={{ required: false }}
            isEditDisable={isEditDisabled || !isDraUploadAccessFeatureFlagEnabled}
          />
        </div>
      ) : (
        <div>
          {isDraUploadAccessFeatureFlagEnabled && !isEditDisabled ? (
            <AgentFormUploadControl
              control={control}
              name={'draCertificate'}
              rules={{ required: false }}
              errors={errors}
              isEditDisabled={false}
              styles={{}}
              setSubmitButtonDisabled={setIsSubmitButtonDisabled}
            />
          ) : null}
        </div>
      )}

      <div className={'pt-4 pl-5 mt-4 flex'}>
        <Typography variant={'h4'} color="var(--navi-color-gray-c2)" className="pr-1">
          Agent Allocation Details
        </Typography>
        <Typography variant="p4" color="var(--navi-color-gray-c3)">
          (for next month)
        </Typography>
      </div>

      <div className={styles.inputContainer}>
        <AgentFormRadioChip
          control={control}
          name={'agentType'}
          rules={{ required: false }}
          errors={errors}
          isEditDisabled={isEditDisabled}
          styles={styles}
          inputLabel={'Agent Type'}
          defaultValue={
            agentData?.agentAllocationProfileView?.currentMonthAllocationView?.metadata
              ?.agentBandwidthType || ''
          }
          showRequiredIcon={false}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormTextbox
          control={control}
          name="nextMonthAgentPincode"
          rules={{
            required: false,
            minLength: 6,
            maxLength: 6,
            pattern: /^[0-9]{6}$/i
          }}
          inputLabel={'Agent Pincode'}
          placeholder={'Enter 6-digit pincode'}
          isEditDisabled={isEditDisabled}
          errors={errors}
          styles={styles}
          validationMessage={'Enter 6 digit valid pincode'}
          onChangeCallback={handleAgentPincodeChange}
          isRequired={false}
        />
      </div>

      {isPincodeMismatch && (
        <WarningSection
          message={VERIFICATION_MESSAGE.MISMATCHED_PINCODE}
          color={'var(--grayscale-2)'}
          iconColor={'var(--grayscale-2)'}
          showTriangle={false}
          bgColor="var(--blue-bg)"
        />
      )}

      <AgentBaseLocation
        latitude={'nextMonthLatitude'}
        longitude={'nextMonthLongitude'}
        control={control}
        errors={errors}
        isEditDisabled={isEditDisabled}
        callbackFn={handleCoordinatesChange}
        showCurrentMonthLabel={false}
      />

      <VerificationComponent
        getValues={getValues}
        locationVerificationStatus={locationVerificationStatus}
        showLocationVerificationToast={showLocationVerificationToast}
        isEditDisabled={isEditDisabled}
        reverseGeocodedAgentCity={reverseGeocodedAgentCity}
        reverseGeocodedAgentPincode={reverseGeocodedAgentPincode}
        handleVerification={handlePincodeVerification}
        showVerifyButton={showVerifyButton}
        showCityPincode={showCityPincode}
        isMisMatchedState={isMisMatchedPincode}
        warningMessage={VERIFICATION_MESSAGE.MISMATCHED_PINCODE_LOCATION}
      />
    </>
  );
};

export default ExternalFieldAgentForm;
